import { Injectable } from '@angular/core';
import {
  AccountInfo,
  BrowserCacheLocation,
  Configuration,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * External ID (CIAM) sign-in: contractors and staff of customers who do not bring their own
 * directory. Email + password accounts in our one CIAM tenant (plan OD-2, N7: no MFA for now).
 *
 * ── A SECOND, SEPARATE MSAL INSTANCE ──────────────────────────────────────────────────────────
 * The workforce sign-in is msal-angular's MSAL_INSTANCE (app.module.ts), authority
 * login.microsoftonline.com/common. CIAM is a different authority and a different app (633c6a40),
 * so it gets its own PublicClientApplication, owned here and created only on first use.
 *
 * ── ITS CACHE IS sessionStorage, AND THAT IS LOAD-BEARING ─────────────────────────────────────
 * MSAL keys cached ACCOUNTS by user, environment and tenant, not by client id. The workforce instance
 * caches in localStorage. If this instance shared localStorage, a CIAM account would appear in the
 * workforce instance's getAllAccounts(). The interceptor would then take the CIAM user for a
 * workforce session and try to acquire an Entra token for it: a failed silent call and an
 * interactive redirect to the wrong authority. A different storage area keeps the two caches unable
 * to see each other. Cost: a CIAM session lasts for the tab, not across tabs. Acceptable.
 *
 * ── A FULL-PAGE REDIRECT, NOT A POPUP (Joel, hybrid-dev 2026-10-05) ──────────────────────────────────
 * It was a popup, chosen so the CIAM response would never land where the WORKFORCE instance's
 * redirect handling runs. Joel's first real sign-in rejected the popup ("do we need the modal?"; his
 * password manager saved the password under "Ciamlogin" from a bare popup). The race the popup avoided
 * does not exist in msal-browser 4.29.0: StandardController.handleRedirectPromiseInternal returns null,
 * WITHOUT touching the URL, unless `isInteractionInProgress(true)`, i.e. unless the shared
 * interaction-status flag names ITS OWN client id. During a CIAM redirect the flag names 633c6a40, so
 * the workforce instance (2e945b52) steps aside and only this instance consumes the response.
 *
 * The round trip: signIn() -> loginRedirect -> the CIAM authority -> back to redirectUri (the bare
 * origin, the only SPA URI registered on 633c6a40) -> AppComponent calls handleRedirect() BEFORE the
 * router's first navigation -> the account is published -> AppComponent sends the router to the page
 * the sign-in started from. navigateToLoginRequestUrl is OFF so MSAL does not do a second full page
 * load to get there; the app routes itself.
 *
 * WHAT SURVIVES THE ROUND TRIP, and where: the invite link (customer id, invite id, secret) stays in
 * sessionStorage under `vericurePendingLink` (pending-link.ts), put there when the link was opened and
 * already stripped from the address bar; it is cleared on accept or on a final refusal. The return
 * path is one sessionStorage key, `vericureExternalIdReturn` (a path like "/invite", no query, no
 * secret), cleared as soon as it is read. MSAL keeps its own PKCE verifier and request state in this
 * instance's sessionStorage while the redirect is in flight, and clears them on return. Nothing in
 * the URL, nothing in localStorage, nothing logged.
 *
 * ── SELF-GATING ───────────────────────────────────────────────────────────────────────────────
 * environment.externalIdClientId empty ⇒ enabled() is false, no instance is created, and nothing
 * contacts ciamlogin.com. Same rule as GoogleIdentityService.
 */
/** One sessionStorage key: the PATH to return to after a CIAM redirect. Never a query or a secret. */
export const EXTERNAL_ID_RETURN_KEY = 'vericureExternalIdReturn';

/** A same-origin PATH: one leading slash ("//host" is protocol-relative, i.e. another site). */
export function isLocalPath(path: string | null | undefined): path is string {
  return !!path && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !/[?#]/.test(path);
}

function rememberReturnPath(path: string): void {
  const clean = path.split(/[?#]/)[0];
  if (!isLocalPath(clean)) return;
  try {
    sessionStorage.setItem(EXTERNAL_ID_RETURN_KEY, clean);
  } catch {
    /* storage blocked: the app lands on its default route instead */
  }
}

/**
 * Set by signOut(), cleared by the next completed sign-in: while set, sign-in sends prompt=login.
 * localStorage, not sessionStorage, because the IdP cookie it guards against is not per tab either.
 * A flag only, never an identity.
 */
export const EXTERNAL_ID_SIGNED_OUT_KEY = 'vericureExternalIdSignedOut';

function rememberSignedOut(): void {
  try {
    localStorage.setItem(EXTERNAL_ID_SIGNED_OUT_KEY, '1');
  } catch {
    /* storage blocked: the end-session redirect alone has to do it */
  }
}

function signedOutHere(): boolean {
  try {
    return localStorage.getItem(EXTERNAL_ID_SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function forgetSignedOut(): void {
  try {
    localStorage.removeItem(EXTERNAL_ID_SIGNED_OUT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * The ONLY pages a CIAM sign-in may return to. A stored value that is anything else (a tampered
 * key, a stale path) lands on "/" instead: the key must never become an open or arbitrary redirect.
 */
export const EXTERNAL_ID_RETURN_PATHS: readonly string[] = ['/invite', '/'];

/** Read AND clear the return path: an allow-listed path, "/" for anything else, null when none was stored. */
export function takeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(EXTERNAL_ID_RETURN_KEY);
    sessionStorage.removeItem(EXTERNAL_ID_RETURN_KEY);
    if (path === null) return null;
    return isLocalPath(path) && EXTERNAL_ID_RETURN_PATHS.includes(path) ? path : '/';
  } catch {
    return null;
  }
}

/**
 * The CIAM instance's MSAL configuration, from the environment alone (config-driven: switching the
 * authority, e.g. to the login.vericure.io custom domain, is an environment change, not a code change).
 */
export function externalIdMsalConfig(clientId: string, authority: string, origin: string): Configuration {
  return {
    auth: {
      clientId,
      authority,
      // MSAL only trusts login.microsoftonline.com-family hosts by default; trust exactly this one.
      knownAuthorities: [new URL(authority).host],
      // Must equal a registered SPA URI exactly. Registered on 633c6a40: the bare origins.
      redirectUri: origin,
      // The app routes itself after handleRedirect() (AppComponent); a second full page load to the
      // start page would only cost time.
      navigateToLoginRequestUrl: false,
    },
    cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
  };
}

/** The client id the shared MSAL interaction flag names, or null (key name is msal.interaction.status in 4.29.0). */
function interactionFlagClientId(storage: Storage): string | null {
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.endsWith('interaction.status')) {
        return (JSON.parse(storage.getItem(key) ?? 'null') as { clientId?: string } | null)?.clientId ?? null;
      }
    }
  } catch {
    /* unreadable: treat as no flag */
  }
  return null;
}

/**
 * PUT THE RETURN PAGE IN THE ADDRESS BAR BEFORE ANGULAR STARTS (call from main.ts).
 *
 * Measured on hybrid-dev, 2026-10-05: this app's router navigates to the page's own URL during
 * bootstrap, before AppComponent's redirect handling runs (a plain cold load of "/" pushes /login
 * the same way). On a CIAM return that URL is the bare origin, so that navigation went "/" ->
 * userAuthGuard -> /login, released the instant MSAL cleared the fragment, and the invitee landed
 * on the STAFF sign-in page instead of the invite. Rewriting only the PATH here, before bootstrap,
 * makes that first navigation go to "/invite"; the fragment stays for MSAL to read.
 *
 * Acts only when ALL hold: CIAM is configured; the URL carries an auth response; the shared MSAL
 * interaction flag names the CIAM client (so a WORKFORCE return is never touched); and an
 * allow-listed return path is stored. The key is NOT consumed here (AppComponent's handleRedirect
 * reads and clears it).
 */
export function routeExternalIdReturnBeforeBootstrap(win: Window = window): string | null {
  const env = environment as { externalIdClientId?: string; externalIdAuthority?: string };
  if (!env.externalIdClientId || !env.externalIdAuthority) return null;
  try {
    if (!/(^|[#&])(code|error)=/.test(win.location.hash) || !/(^|[#&])state=/.test(win.location.hash)) return null;
    if (interactionFlagClientId(win.sessionStorage) !== env.externalIdClientId) return null;
    const path = win.sessionStorage.getItem(EXTERNAL_ID_RETURN_KEY);
    if (!isLocalPath(path) || !EXTERNAL_ID_RETURN_PATHS.includes(path)) return null;
    if (win.location.pathname === path) return null;
    win.history.replaceState(win.history.state, '', path + win.location.hash);
    return path;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class ExternalIdSignInService {
  private instance: Promise<IPublicClientApplication> | null = null;

  private readonly _account = new BehaviorSubject<AccountInfo | null>(null);
  readonly account$: Observable<AccountInfo | null> = this._account.asObservable();

  private get env(): { externalIdClientId?: string; externalIdAuthority?: string } {
    return environment as { externalIdClientId?: string; externalIdAuthority?: string };
  }

  enabled(): boolean {
    return !!this.env.externalIdClientId && !!this.env.externalIdAuthority;
  }

  /** The API scope. Same app as the client: CIAM tokens carry aud = 633c6a40 (DataServices ExternalIdAudience). */
  scope(): string {
    return `api://${this.env.externalIdClientId}/access_as_user`;
  }

  account(): AccountInfo | null {
    return this._account.value;
  }

  /**
   * Restore a session from this tab's cache. Called by the interceptor before it decides there is
   * no session, so a page reload inside the tab does not look signed out.
   */
  async restore(): Promise<AccountInfo | null> {
    if (!this.enabled()) {
      return null;
    }
    const pca = await this.pca();
    // FINISH ANY REDIRECT RETURN FIRST (2026-10-06). userAuthGuard waits on restore() before it
    // decides, and on a CIAM return to "/" it used to ask here BEFORE the response was consumed:
    // getAllAccounts() was still empty, so a just-signed-in user was sent to /login. MSAL memoises
    // handleRedirectPromise per hash, so this shares the one promise AppComponent's handleRedirect()
    // awaits; it does not process the response twice. A failure is AppComponent's to report.
    try {
      await pca.handleRedirectPromise();
    } catch {
      /* reported by handleRedirect() */
    }
    const account = pca.getAllAccounts()[0] ?? null;
    this._account.next(account);
    return account;
  }

  /**
   * Start the External ID sign-in as a full-page redirect. From /login the typed email is the hint;
   * from the invite page there is none, because links/info deliberately
   * returns no email. Resolves only if the redirect could NOT start (the page navigates away
   * otherwise); the return is handled by handleRedirect() at the next app start.
   *
   * @param returnPath where to land after sign-in, e.g. "/invite". A path only: never a query.
   * @param loginHint the email the user typed on /login; never stored here.
   */
  async signIn(returnPath: string, loginHint?: string): Promise<void> {
    if (!this.enabled()) {
      return;
    }
    const pca = await this.pca();
    rememberReturnPath(returnPath);
    // WITH A HINT (the one-box /login): prefill the address and DROP prompt. Measured on
    // login.vericure.io 2026-10-07: login_hint prefills the sign-in page, but prompt=select_account
    // alongside it inserts a "Pick an account" step. WITHOUT a hint (the invite page, where
    // links/info deliberately returns no email): unchanged, select_account.
    // AFTER A SIGN-OUT, ASK FOR THE PASSWORD (Joel, hybrid-dev 2026-10-08). signOut() ends the IdP
    // session too, but if that leg failed or was skipped the CIAM cookie would answer silently, and
    // "signed out" would mean nothing. prompt=login forces credentials once; the flag clears when a
    // sign-in comes back.
    const afterSignOut = signedOutHere();
    await pca.loginRedirect(
      afterSignOut
        ? { scopes: [this.scope()], prompt: 'login', ...(loginHint ? { loginHint } : {}) }
        : loginHint
          ? { scopes: [this.scope()], loginHint }
          : { scopes: [this.scope()], prompt: 'select_account' }
    );
  }

  /**
   * Consume a CIAM redirect response, if this page load carries one. Call once, at app start,
   * BEFORE the router navigates. Returns where the app should go next ("/invite"), or null when
   * this load was not the end of a CIAM sign-in. A no-op when CIAM is unconfigured.
   */
  async handleRedirect(): Promise<string | null> {
    if (!this.enabled()) {
      return null;
    }
    const pca = await this.pca();
    try {
      const result = await pca.handleRedirectPromise();
      if (!result?.account) {
        return null;
      }
      this._account.next(result.account);
      forgetSignedOut();
      // Signed in: go back to where it started, or to the app's root.
      return takeReturnPath() ?? '/';
    } catch (err) {
      // A refused or abandoned sign-in (e.g. the user backed out at the authority) still comes back
      // HERE: send them to the page they started from, where they can try again. The message says
      // what failed; it carries no token and no secret.
      console.warn('External ID sign-in did not complete', (err as { errorCode?: string })?.errorCode ?? err);
      return takeReturnPath();
    }
  }

  /**
   * A token for OUR API only. Silent; null when there is no CIAM session or it cannot be renewed
   * silently. Never interactive: a popup must come from a user gesture, which an interceptor is not.
   */
  async accessToken(): Promise<string | null> {
    if (!this.enabled()) {
      return null;
    }
    const account = this.account() ?? (await this.restore());
    if (!account) {
      return null;
    }
    const pca = await this.pca();
    try {
      const result = await pca.acquireTokenSilent({ scopes: [this.scope()], account });
      return result.accessToken || null;
    } catch {
      return null;
    }
  }

  /**
   * End the CIAM session: this tab's cache AND the session at the authority.
   *
   * Resolves true when the page is navigating to the authority's end-session endpoint (the caller
   * must not navigate as well; the authority returns to the bare origin, whose guard sends a
   * signed-out user to /login), false when it is not (CIAM unconfigured, or the redirect could not
   * start: the caller navigates).
   *
   * WHY THE AUTHORITY TOO (Joel, hybrid-dev 2026-10-08). This used to be clearCache() alone. The
   * login.vericure.io session cookie outlived it, so the next sign-in, which sends no prompt when
   * the email is the hint, was answered silently: "signed out", then straight back in with no
   * password. A real sign-in with a real token, so not a bypass, but not what "sign out" means.
   *
   * `{ atAuthority: false }` is for the invite page's "use a different account": the user stays on
   * the page, so no redirect. The cache still goes, and the next sign-in still sends prompt=login,
   * which is what lets them type a different account.
   */
  async signOut(opts: { atAuthority?: boolean } = {}): Promise<boolean> {
    if (!this.enabled()) {
      return false;
    }
    rememberSignedOut();
    const pca = await this.pca();
    const account = this.account() ?? pca.getAllAccounts()[0] ?? null;
    this._account.next(null);
    // Cleared before leaving, so nothing in this tab can still hand out a token while the page
    // navigates (logoutRedirect clears it too, but only once it gets that far).
    // ALL accounts, not just this one (lead N1, Joel's go 2026-10-08): a second CIAM account cached
    // in this tab would otherwise survive "sign out" and be restored on the next load. The account
    // captured above still goes to logoutRedirect, as the authority's logout hint.
    await pca.clearCache();
    if (opts.atAuthority === false) {
      return false;
    }
    try {
      // The bare origin: the only post-logout URI registered on 633c6a40, the same as redirectUri.
      await pca.logoutRedirect({ account: account ?? undefined, postLogoutRedirectUri: window.location.origin });
      return true;
    } catch (err) {
      // The cache is already gone and the next sign-in asks for the password (rememberSignedOut), so a
      // failure here still leaves nobody signed in silently.
      console.warn('External ID sign-out at the authority did not start', (err as { errorCode?: string })?.errorCode ?? err);
      return false;
    }
  }

  private pca(): Promise<IPublicClientApplication> {
    if (!this.instance) {
      this.instance = (async () => {
        const pca = new PublicClientApplication(
          externalIdMsalConfig(this.env.externalIdClientId!, this.env.externalIdAuthority!, window.location.origin)
        );
        await pca.initialize();
        return pca;
      })();
    }
    return this.instance;
  }
}
